import { useEffect, useMemo, useState } from 'react';
import { Search } from '@carbon/react';
import { Rocket } from '@carbon/react/icons';
import { SidePanel } from '@carbon/ibm-products';
import { formatTcWorkspaceDate } from 'utils/dates';
import type { AreaWorkspaceSummaryItem } from 'types/area-workspace';
// Registers the ibm-products prefix the SidePanel renders under, the same import
// the pre-drill drawer makes.
import 'components/inline-edit/ibm-products-config';
import styles from './area-workspaces-drawer.module.scss';

export interface AreaWorkspacesDrawerProps {
  open: boolean;
  /** The area the workspaces were opened on, named in the subtitle. */
  areaName: string;
  /** Every workspace on the area, as the area payload carries them. */
  workspaces: AreaWorkspaceSummaryItem[];
  onClose: () => void;
  /** Opens one workspace. The caller routes; this drawer only reports the choice. */
  onOpenWorkspace: (workspace: AreaWorkspaceSummaryItem) => void;
}

// The workspaces opened on an area, in the drawer the pre-drill launcher uses for its
// own list. Everything shown comes off the area payload, so opening costs no request.
export const AreaWorkspacesDrawer = ({
  open,
  areaName,
  workspaces,
  onClose,
  onOpenWorkspace,
}: AreaWorkspacesDrawerProps): JSX.Element => {
  const [query, setQuery] = useState('');

  // The search box starts empty each time the drawer opens, rather than reopening
  // on a filter the user set a visit ago.
  useEffect(() => {
    if (open) {
      setQuery('');
    }
  }, [open]);

  // SidePanel pushes #main-content via inline margin and only clears it on open→false.
  // This unmounts while open when the page hands over to a workspace, so clear the push
  // on unmount.
  useEffect(
    () => () => {
      const pageContent = document.querySelector('#main-content');
      if (pageContent instanceof HTMLElement) {
        pageContent.style.marginInlineEnd = '';
        pageContent.style.marginInlineStart = '';
        pageContent.style.inlineSize = '';
      }
    },
    []
  );

  // Newest first: the summary arrives unordered, and the workspace someone wants
  // is almost always the one they just opened.
  const sorted = useMemo(
    () =>
      [...workspaces].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
    [workspaces]
  );

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) {
      return sorted;
    }
    return sorted.filter((workspace) =>
      workspace.name.toLowerCase().includes(needle)
    );
  }, [query, sorted]);

  return (
    <SidePanel
      open={open}
      onRequestClose={onClose}
      title="Area Workspaces"
      subtitle={`${workspaces.length} ${
        workspaces.length === 1 ? 'workspace' : 'workspaces'
      } · ${areaName}`}
      slideIn
      selectorPageContent="#main-content"
      placement="right"
      size="md"
    >
      <div className={styles.body}>
        <p className={styles.description}>
          Workspaces opened on this area. Each one holds its own review, and
          promoting from a workspace is what puts the area on the map.
        </p>
        <Search
          size="md"
          labelText="Search workspaces"
          placeholder="Search workspaces"
          value={query}
          onChange={(event) => setQuery(event.target.value ?? '')}
        />
        <div className={styles.list}>
          {filtered.length === 0 ? (
            <div className={styles.empty}>No workspaces found</div>
          ) : (
            filtered.map((workspace) => (
              <button
                type="button"
                key={workspace.id}
                className={styles.row}
                onClick={() => onOpenWorkspace(workspace)}
              >
                <span className={styles.rowTitle}>
                  <span className={styles.rowName}>{workspace.name}</span>
                  {workspace.promoted && (
                    <span className={styles.promoted}>
                      <Rocket size={16} aria-hidden />
                      Promoted
                    </span>
                  )}
                  {/* Minted by the server when the area was created, rather than
                      opened by hand — worth saying, since nobody will recognise it. */}
                  {workspace.systemGenerated && (
                    <span className={styles.system}>System</span>
                  )}
                </span>
                {workspace.createdBy && (
                  <span className={styles.rowCreator}>
                    {workspace.createdBy}
                  </span>
                )}
                <span className={styles.rowDates}>
                  Created: {formatTcWorkspaceDate(workspace.createdAt)} |
                  Updated: {formatTcWorkspaceDate(workspace.updatedAt)}
                </span>
              </button>
            ))
          )}
        </div>
      </div>
    </SidePanel>
  );
};
